import '@mux/mux-player';

import type MuxPlayerElement from '@mux/mux-player';

import type { Track } from '../models/album.interface';

export interface AudioEngineSnapshot {
  playbackId: string | null;
  currentTime: number;
  paused: boolean;
  duration: number;
  claimedBy: string | null;
}

/**
 * Singleton audio pipeline that lives on document.body, outside any app-block
 * DOM. Shopify themes replace/morph a section's DOM on variant selection,
 * destroying everything inside it — a mux player rendered inside the block
 * dies mid-song (SNGL-7020). Components act as controllers: they attach
 * listeners on mount and detach on unmount; the engine (and the audio)
 * survives the remount, and the new mount adopts it via `snapshot()`.
 *
 * If no controller re-attaches shortly after the last one detaches (a real
 * teardown, not a section re-render), playback pauses so audio is never left
 * running with no visible player bar.
 */
class AudioEngine {
  readonly el: MuxPlayerElement;

  private appliedPlaybackId: string | null = null;
  private appliedToken: string | null = null;
  private controllers = 0;
  private orphanPauseTimer = 0;
  private owner: string | null = null;

  constructor() {
    const host = document.createElement('div');
    host.setAttribute('data-single-audio-engine', '');
    host.style.display = 'none';

    const el = document.createElement('mux-player') as MuxPlayerElement;
    el.setAttribute('audio', '');
    el.setAttribute('stream-type', 'on-demand');
    el.setAttribute('player-software-name', 'single-audio-player');
    el.setAttribute('player-init-time', String(Date.now()));

    host.appendChild(el);
    document.body.appendChild(host);
    this.el = el;
  }

  get currentPlaybackId(): string | null {
    return this.appliedPlaybackId;
  }

  get claimedBy(): string | null {
    return this.owner;
  }

  /**
   * Take ownership of the engine before applying a source and playing. There
   * is one audio element, so exactly one controller may treat engine events as
   * its own; everyone else must render a paused state until they reclaim. Call
   * synchronously inside the user-gesture handler so play() stays
   * gesture-bound on iOS. A null/`'bar'` owner is the album player bar —
   * pre-ownership controllers (bar instances, tracklist app blocks) treat both
   * as "theirs".
   */
  claim(ownerId: string): void {
    if (this.owner === ownerId) return;
    const previousOwner = this.owner;
    this.owner = ownerId;
    this.el.dispatchEvent(
      new CustomEvent('ownerchange', { detail: { previousOwner, owner: ownerId } })
    );
  }

  /**
   * Point the engine at a track's source. No-op when the source is unchanged.
   * Returns whether a new load was triggered — a play() issued in the same
   * tick as a new load gets rejected with AbortError, so callers must also
   * retry from a one-shot `canplay` listener when this returns true.
   */
  applyTrack(track: Pick<Track, 'playbackId' | 'token'> | null): boolean {
    const playbackId = track?.playbackId ?? null;
    const token = track?.token ?? null;
    if (playbackId === this.appliedPlaybackId && token === this.appliedToken) return false;

    this.appliedPlaybackId = playbackId;
    this.appliedToken = token;
    // Token first so the load triggered by the playbackId change is signed.
    this.el.tokens = token ? { playback: token } : {};
    this.el.playbackId = playbackId ?? undefined;
    return true;
  }

  setMetadata(metadata: Record<string, string | undefined>): void {
    this.el.metadata = metadata;
  }

  on(type: string, handler: EventListener): () => void {
    this.el.addEventListener(type, handler);
    return () => this.el.removeEventListener(type, handler);
  }

  /** A controller (player component) is attached. */
  retain(): void {
    this.controllers++;
    clearTimeout(this.orphanPauseTimer);
  }

  /**
   * A controller detached. During a section re-render the replacement mounts
   * within moments; only pause when nothing re-attaches (real teardown).
   */
  release(): void {
    this.controllers = Math.max(0, this.controllers - 1);
    if (this.controllers > 0) return;
    clearTimeout(this.orphanPauseTimer);
    this.orphanPauseTimer = window.setTimeout(() => {
      if (this.controllers === 0) this.el.pause();
    }, 2000);
  }

  snapshot(): AudioEngineSnapshot {
    return {
      playbackId: this.appliedPlaybackId,
      currentTime: this.el.currentTime ?? 0,
      paused: this.el.paused ?? true,
      duration: this.el.duration ?? 0,
      claimedBy: this.owner,
    };
  }
}

// Window-scoped so a double execution of the app bundle (themes re-run the
// script tags delivered in Section Rendering API responses) can't create a
// second audio pipeline.
declare global {
  interface Window {
    __singleAudioEngine?: AudioEngine;
  }
}

export function getAudioEngine(): AudioEngine {
  return (window.__singleAudioEngine ??= new AudioEngine());
}

/** Read the engine's state without creating one. */
export function peekAudioEngineSnapshot(): AudioEngineSnapshot | null {
  return window.__singleAudioEngine?.snapshot() ?? null;
}
